import { useEffect, useState } from 'react';
import { apiService } from '../services/api';

/**
 * usePlan — subscription/entitlement hook.
 *
 * Fetches GET /billing/entitlement ({plan, status, currentPeriodEnd}) once
 * per browser session: a module-level cache plus in-flight-promise dedupe
 * mean the first mounted consumer (badge, gates, pricing page, ...) triggers
 * the network call and every other consumer — mounted at the same time or
 * later — reuses that same cache/promise instead of re-fetching.
 *
 * Fallback semantics (IMPORTANT): while the fetch is in flight, AND on any
 * fetch error, this returns `plan: 'plus'`. We never want a transient
 * network hiccup to flash locks/gates at a paying (or any) user — the
 * server is the actual enforcement point for plan-gated writes, so an
 * over-permissive client-side default here is safe; an under-permissive one
 * just annoys users for no security benefit.
 *
 * Export shape is `{ plan, status, currentPeriodEnd, loading }`. Existing
 * consumers (SourceWizard, ScheduleWizard, ScheduleEditor) destructure only
 * `{ plan }` and compare `=== 'free'` — that contract is preserved.
 */

let cachedEntitlement = null; // { plan, status, currentPeriodEnd } once loaded
let inFlightPromise = null;
let fetchFailed = false;
let resolvedFromServer = false; // true ONLY after a successful entitlement fetch — display of paid status requires this
let cachedForSub = null; // the Cognito sub the current cache belongs to (FIX 6)
const listeners = new Set();

function notifyListeners() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (error) {
      console.error('usePlan: listener error', error);
    }
  });
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Retry-with-backoff before settling on the fail-open-to-plus default (§9,
// free-tier-cap-enforcement plan): the fallback exists so a network hiccup never flashes
// locks/gates at users, but that same fallback also hides a real 'waitlisted' status if the
// FIRST fetch happens to be the one that's flaky. Three attempts, short exponential backoff —
// long enough to ride out a transient blip, short enough not to noticeably delay the UI (the
// caller already sees `loading: true` for the whole window).
const ENTITLEMENT_RETRY_DELAYS_MS = [400, 1200];

async function fetchEntitlementWithRetry() {
  let lastError;
  for (let attempt = 0; attempt <= ENTITLEMENT_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await apiService.getEntitlement();
    } catch (error) {
      lastError = error;
      if (attempt < ENTITLEMENT_RETRY_DELAYS_MS.length) {
        await delay(ENTITLEMENT_RETRY_DELAYS_MS[attempt]);
      }
    }
  }
  throw lastError;
}

function fetchEntitlement() {
  if (cachedEntitlement) {
    return Promise.resolve(cachedEntitlement);
  }
  if (inFlightPromise) {
    return inFlightPromise;
  }

  inFlightPromise = fetchEntitlementWithRetry()
    .then((data) => {
      cachedEntitlement = data;
      fetchFailed = false;
      resolvedFromServer = true;
      inFlightPromise = null;
      notifyListeners();
      return data;
    })
    .catch((error) => {
      fetchFailed = true;
      inFlightPromise = null;
      notifyListeners();
      throw error;
    });

  return inFlightPromise;
}

/**
 * Bust the module-level entitlement cache and refetch. Call this after a
 * Paddle checkout closes so the plan badge / waitlist banner / plan gates
 * pick up the new plan without requiring a full page reload.
 */
export function refreshEntitlement() {
  cachedEntitlement = null;
  fetchFailed = false;
  resolvedFromServer = false;
  inFlightPromise = null;
  return fetchEntitlement().catch((error) => {
    console.error('usePlan: refreshEntitlement failed', error);
  });
}

/**
 * Reset the module-level entitlement cache when the signed-in user changes (FIX 6). The cache is
 * process-global (module scope), so without this a second user in the same tab session (login →
 * logout → login as someone else, no page reload) would read the first user's plan. Guarded on the
 * sub: a re-render with the SAME user is a no-op, so a good cache / in-flight fetch is never
 * dropped. Recording cachedForSub here marks which user the (re)fetch that follows belongs to.
 */
export function resetEntitlementForUser(sub) {
  if (cachedForSub === sub) return;
  cachedEntitlement = null;
  resolvedFromServer = false;
  inFlightPromise = null;
  fetchFailed = false;
  cachedForSub = sub;
  notifyListeners();
}

/**
 * The currently cached entitlement status, if any has resolved yet. Used by the heartbeat
 * wiring (hooks/useHeartbeat.js) to decide whether a status change reported by
 * POST /user/heartbeat is actually new before paying for a refetch.
 */
export function getCachedEntitlementStatus() {
  return cachedEntitlement ? cachedEntitlement.status : undefined;
}

export function usePlan() {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const onUpdate = () => forceRender((n) => n + 1);
    listeners.add(onUpdate);

    if (!cachedEntitlement) {
      fetchEntitlement().catch((error) => {
        console.error('usePlan: failed to load entitlement', error);
      });
    }

    return () => listeners.delete(onUpdate);
  }, []);

  const loading = !cachedEntitlement && !fetchFailed;

  if (loading || fetchFailed || !cachedEntitlement) {
    // While loading and on any error: never flash locks at users. The
    // server enforces the real limits; this is just UI presentation.
    return { plan: 'plus',
      resolved: false, status: undefined, currentPeriodEnd: undefined, loading };
  }

  return {
    plan: cachedEntitlement.plan || 'plus',
    resolved: resolvedFromServer, // paid-status DISPLAY must require this (2026-07-26 incident)
    status: cachedEntitlement.status,
    currentPeriodEnd: cachedEntitlement.currentPeriodEnd,
    loading: false
  };
}

export default usePlan;
